/**
 * Camera program data model — plain JSON-serializable types shared by the
 * evaluator, UI, exporters and scene/cameras files. All times are in SECONDS
 * (the timeline preserves seconds across fps changes, so seconds-based keys
 * survive an fps switch; frames do not).
 */

export type Vec3 = [number, number, number];

/** Easing applied over the segment leaving a keyframe. 'hold' = step cut. */
export type EasingKey = 'linear' | 'ease' | 'ease-in' | 'ease-out' | 'hold';

export interface CameraKeyframe {
  /** Seconds from clip start. */
  time: number;
  position: Vec3;
  /** Look-at point; authoritative when aim.mode === 'path'. */
  target: Vec3;
  fov: number;
  easing: EasingKey;
}

/** What the camera looks at. 'path' = the keyframed target values. */
export type CameraAim =
  | { mode: 'path' }
  | { mode: 'point'; point: Vec3 }
  | { mode: 'bone'; actorId: string; bone: string; heightOffset: number }
  | { mode: 'group'; actorIds: string[] | 'all' };

/** Deterministic handheld-shake layer added on top of keyframe interpolation. */
export interface ShakeLayer {
  enabled: boolean;
  /** Position amplitude in metres. */
  posAmp: number;
  /** Aim-offset amplitude in degrees. */
  rotAmp: number;
  /** Sway frequency in Hz. */
  freq: number;
  /** Noise seed — keeps preview and export frame-identical. */
  seed: number;
}

export interface CameraDef {
  id: string;
  name: string;
  /** Tint used for the strip block, path line and frustum helper. */
  color: number;
  /** Interpolation between position/target keys. */
  interp: 'smooth' | 'linear';
  /** Sorted by time; a single keyframe means a static camera. */
  keyframes: CameraKeyframe[];
  aim: CameraAim;
  shake: ShakeLayer | null;
  /** Preset provenance so its sliders can be re-tuned and re-baked. */
  preset: { id: string; params: Record<string, number>; subjectActorId: string | null } | null;
}

/** A cut point: from `time` (seconds) onward, the program shows `cameraId`. */
export interface CutPoint {
  time: number;
  cameraId: string;
}

export interface CameraProgram {
  version: 1;
  cameras: CameraDef[];
  /** Sorted cut points; shots[0].time is 0 whenever non-empty. */
  shots: CutPoint[];
}

/** Standalone `*.cameras.json` payload, reusable across scenes. */
export interface CamerasFile {
  app: 'aka-mocap-viewer';
  kind: 'cameras';
  version: 1;
  program: CameraProgram;
}

export function createEmptyProgram(): CameraProgram {
  return { version: 1, cameras: [], shots: [] };
}

/** Palette for camera tints (distinct from the actor palette hues). */
export const CAMERA_COLORS = [
  0x4f9cff, 0xffb347, 0x5fd68a, 0xe66ad2, 0x53d0d8, 0xf2e04e, 0xff7a6e, 0xa98bff,
];

export function nextCameraColor(program: CameraProgram): number {
  return CAMERA_COLORS[program.cameras.length % CAMERA_COLORS.length];
}

export function nextCameraName(program: CameraProgram): string {
  let n = program.cameras.length + 1;
  const taken = new Set(program.cameras.map((c) => c.name));
  while (taken.has(`Camera ${n}`)) n += 1;
  return `Camera ${n}`;
}

export function findCamera(program: CameraProgram, id: string | null): CameraDef | null {
  if (!id) return null;
  return program.cameras.find((c) => c.id === id) ?? null;
}

/** Keep keyframes sorted by time (stable for equal times). */
export function sortKeyframes(def: CameraDef): void {
  def.keyframes.sort((a, b) => a.time - b.time);
}

/** Keep shots sorted and anchored: first cut always starts at 0. */
export function normalizeShots(program: CameraProgram): void {
  program.shots.sort((a, b) => a.time - b.time);
  if (program.shots.length > 0) program.shots[0].time = 0;
}

/** The cut governing time `t`, or null when there are no shots. */
export function shotAt(program: CameraProgram, t: number): CutPoint | null {
  let active: CutPoint | null = null;
  for (const shot of program.shots) {
    if (shot.time <= t) active = shot;
    else break;
  }
  return active ?? (program.shots.length > 0 ? program.shots[0] : null);
}

/**
 * Validate + coerce a parsed CameraProgram from JSON. Returns a safe deep copy
 * or null if the shape is unusable. Unknown fields are dropped.
 */
export function sanitizeProgram(raw: unknown): CameraProgram | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const obj = raw as Partial<CameraProgram>;
  if (!Array.isArray(obj.cameras) || !Array.isArray(obj.shots)) return null;

  const cameras: CameraDef[] = [];
  for (const c of obj.cameras) {
    const cam = sanitizeCamera(c);
    if (cam) cameras.push(cam);
  }
  const ids = new Set(cameras.map((c) => c.id));
  const shots: CutPoint[] = [];
  for (const s of obj.shots as unknown[]) {
    if (typeof s !== 'object' || s === null) continue;
    const shot = s as Partial<CutPoint>;
    if (typeof shot.time !== 'number' || typeof shot.cameraId !== 'string') continue;
    if (!ids.has(shot.cameraId)) continue;
    shots.push({ time: Math.max(0, shot.time), cameraId: shot.cameraId });
  }
  const program: CameraProgram = { version: 1, cameras, shots };
  normalizeShots(program);
  return program;
}

function sanitizeCamera(raw: unknown): CameraDef | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const c = raw as Partial<CameraDef>;
  if (typeof c.id !== 'string' || !Array.isArray(c.keyframes)) return null;
  const keyframes: CameraKeyframe[] = [];
  for (const k of c.keyframes as unknown[]) {
    const key = sanitizeKeyframe(k);
    if (key) keyframes.push(key);
  }
  if (keyframes.length === 0) return null;
  const def: CameraDef = {
    id: c.id,
    name: typeof c.name === 'string' ? c.name : 'Camera',
    color: typeof c.color === 'number' ? c.color : CAMERA_COLORS[0],
    interp: c.interp === 'linear' ? 'linear' : 'smooth',
    keyframes,
    aim: sanitizeAim(c.aim),
    shake: sanitizeShake(c.shake),
    preset:
      typeof c.preset === 'object' && c.preset !== null && typeof c.preset.id === 'string'
        ? {
            id: c.preset.id,
            params: { ...(c.preset.params ?? {}) },
            subjectActorId:
              typeof c.preset.subjectActorId === 'string' ? c.preset.subjectActorId : null,
          }
        : null,
  };
  sortKeyframes(def);
  return def;
}

const EASINGS: EasingKey[] = ['linear', 'ease', 'ease-in', 'ease-out', 'hold'];

function sanitizeKeyframe(raw: unknown): CameraKeyframe | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const k = raw as Partial<CameraKeyframe>;
  if (typeof k.time !== 'number' || !isVec3(k.position) || !isVec3(k.target)) return null;
  return {
    time: Math.max(0, k.time),
    position: [...k.position] as Vec3,
    target: [...k.target] as Vec3,
    fov: typeof k.fov === 'number' ? k.fov : 45,
    easing: EASINGS.includes(k.easing as EasingKey) ? (k.easing as EasingKey) : 'ease',
  };
}

function sanitizeAim(raw: unknown): CameraAim {
  if (typeof raw !== 'object' || raw === null) return { mode: 'path' };
  const a = raw as { mode?: string } & Record<string, unknown>;
  if (a.mode === 'point' && isVec3(a.point)) {
    return { mode: 'point', point: [...(a.point as Vec3)] as Vec3 };
  }
  if (a.mode === 'bone' && typeof a.actorId === 'string' && typeof a.bone === 'string') {
    return {
      mode: 'bone',
      actorId: a.actorId,
      bone: a.bone,
      heightOffset: typeof a.heightOffset === 'number' ? a.heightOffset : 0,
    };
  }
  if (a.mode === 'group') {
    if (a.actorIds === 'all') return { mode: 'group', actorIds: 'all' };
    if (Array.isArray(a.actorIds) && a.actorIds.every((id) => typeof id === 'string')) {
      return { mode: 'group', actorIds: [...(a.actorIds as string[])] };
    }
  }
  return { mode: 'path' };
}

function sanitizeShake(raw: unknown): ShakeLayer | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const s = raw as Partial<ShakeLayer>;
  return {
    enabled: s.enabled === true,
    posAmp: typeof s.posAmp === 'number' ? s.posAmp : 0.04,
    rotAmp: typeof s.rotAmp === 'number' ? s.rotAmp : 1.5,
    freq: typeof s.freq === 'number' ? s.freq : 1.8,
    seed: typeof s.seed === 'number' ? s.seed : 1,
  };
}

function isVec3(v: unknown): v is Vec3 {
  return Array.isArray(v) && v.length === 3 && v.every((n) => typeof n === 'number');
}
